import flow from 'lodash/fp/flow';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import snakeCase from 'lodash/snakeCase';
import toUpper from 'lodash/toUpper';

import type { CustomizationAdditionalFilters } from '@lumapps/customizations/types';

import { FormattedSiteFilter, FormattedTagFilter, GenericListFilterType, NGIFilterId } from '../types';

/**
 * Format an object of filters to make it compatible with the /block API
 * @param filters
 * @returns an object that will be translated as parameters for the block API call
 */
export const formatGenericFiltersForApi = (
    filters?: Partial<GenericListFilterType> & CustomizationAdditionalFilters,
) => {
    if (!isEmpty(filters)) {
        const {
            searchQuery,
            metadata,
            ignoredDefaultFilters,
            tab,
            tags,
            site,
            onlyHighlighted,
            publicationDates,
            author,
            metadataIds: customizationMetadataIds,
            tagsIds: customizationTagsIds,
            siteIds: customizationSiteIds,
        } = filters as Partial<GenericListFilterType> & CustomizationAdditionalFilters;

        const extractedMetadataIds = metadata?.map((metadata) => metadata.id);
        const metadataIds =
            customizationMetadataIds || extractedMetadataIds
                ? [...(extractedMetadataIds || []), ...(customizationMetadataIds || [])]
                : undefined;
        /** Sanitizing search query to avoid injecting filter through the keyword filter */
        const searchQuerySanitized = searchQuery?.replace(/[?&:=[\]{}<>]/g, '');
        const extractedTagsIds = tags?.map((tag: FormattedTagFilter) => tag.id);
        const tagsIds =
            customizationTagsIds || extractedTagsIds
                ? [...(extractedTagsIds || []), ...(customizationTagsIds || [])]
                : undefined;
        const extractedSiteIds = site?.map((site: FormattedSiteFilter) => site.id);
        const siteIds =
            customizationSiteIds || extractedSiteIds
                ? [...(extractedSiteIds || []), ...(customizationSiteIds || [])]
                : undefined;
        const formatedIgnoredDefaultFilters = ignoredDefaultFilters
            ?.filter((filterId) => {
                switch (filterId) {
                    /**
                     * We cannot ignore default filters, for the site. It would trigger a search in ALL the instances.
                     * */
                    case NGIFilterId.site:
                        return !isEmpty(filters?.[filterId]);
                    default:
                        return true;
                }
            })
            .map(flow([snakeCase, toUpper]));
        const { endDate, startDate } = publicationDates || {};

        return omitBy(
            {
                searchQuery: searchQuerySanitized,
                metadataIds,
                tab,
                ignoredDefaultFilters: formatedIgnoredDefaultFilters,
                onlyHighlighted,
                tagsIds,
                siteIds,
                endDate,
                startDate,
                author: author?.email,
            },
            isNil,
        );
    }
    return {};
};
